import { useMutation } from "@tanstack/react-query";
import useApiHelper from "../../hooks/useApiHelper";
import { IDefaultResponse } from "../../models/DefaultResponse";

export default function useToolMutations() {
  const { get, post, put } = useApiHelper();

  const createFirebaseUser = useMutation({
    mutationFn: (req: { name: string; email: string; password: string }) =>
      post<IDefaultResponse<any>, typeof req>("/tools/firebase/users", req),
  });

  const getFirebaseClaims = useMutation({
    mutationFn: (req: { userId: string }) =>
      get<IDefaultResponse<Record<string, unknown>>>(`/tools/firebase/users/${req.userId}/claims`),
  });

  const updateFirebaseClaims = useMutation({
    mutationFn: (req: { userId: string; isAdmin: boolean }) =>
      put<IDefaultResponse<any>, { isAdmin: boolean }>(
        `/tools/firebase/users/${req.userId}/claims`,
        { isAdmin: req.isAdmin }
      ),
  });

  const firebaseLogin = useMutation({
    mutationFn: (req: { email: string; password: string }) =>
      post<IDefaultResponse<any>, typeof req>("/tools/firebase/login", req),
  });

  const generateApplicationRegistrationSecret = useMutation({
    mutationFn: () => post<IDefaultResponse<any>, Record<string, never>>("/tools/secrets/application-registration", {}),
  });

  const convertRgbToInt = useMutation({
    mutationFn: (req: { r: number; g: number; b: number }) =>
      post<IDefaultResponse<any>, typeof req>("/tools/colour/rgb-to-int", req),
  });

  const generateCustomerSigningKey = useMutation({
    mutationFn: () => post<IDefaultResponse<any>, Record<string, never>>("/tools/customers/signing-key", {}),
  });

  const generateApplicationToken = useMutation({
    mutationFn: (req: { applicationId: string; customerId: string; tokenAction: number }) =>
      post<IDefaultResponse<any>, typeof req>("/tools/tokens/application", req),
  });

  const generateAppSettingsApplicationToken = useMutation({
    mutationFn: (req: { applicationId: string }) =>
      post<IDefaultResponse<any>, typeof req>("/tools/tokens/app-settings/application", req),
  });

  const generateAppSettingsApplicationRegistrationToken = useMutation({
    mutationFn: (req: { applicationRegistrationId: string }) =>
      post<IDefaultResponse<any>, typeof req>("/tools/tokens/app-settings/application-registration", req),
  });

  const generateCacheRefreshToken = useMutation({
    mutationFn: (req: { applicationId: string }) =>
      post<IDefaultResponse<any>, typeof req>("/tools/tokens/cache-refresh", req),
  });

  const generateVehicleLookupToken = useMutation({
    mutationFn: (req: { applicationId: string }) =>
      post<IDefaultResponse<any>, typeof req>("/tools/tokens/vehicle-lookup", req),
  });

  const runGtMotivePartsLookup = useMutation({
    mutationFn: (req: { estimateId?: string; vin?: string; cupis?: string[] }) =>
      post<IDefaultResponse<any>, typeof req>("/tools/gt-motive/parts-lookup", req),
  });

  const exportGtMotiveCache = useMutation({
    mutationFn: () => post<IDefaultResponse<any>, Record<string, never>>("/tools/gt-motive/cache-export", {}),
  });

  const importEbayImages = useMutation({
    mutationFn: (req: { itemId?: number; batchSize?: number }) =>
      post<IDefaultResponse<any>, typeof req>("/tools/parts/import-ebay-images", req),
  });

  return {
    createFirebaseUser,
    getFirebaseClaims,
    updateFirebaseClaims,
    firebaseLogin,
    generateApplicationRegistrationSecret,
    convertRgbToInt,
    generateCustomerSigningKey,
    generateApplicationToken,
    generateAppSettingsApplicationToken,
    generateAppSettingsApplicationRegistrationToken,
    generateCacheRefreshToken,
    generateVehicleLookupToken,
    runGtMotivePartsLookup,
    exportGtMotiveCache,
    importEbayImages,
  };
}
